import {
  ErrorSubcode,
  type DeliverableConversationTurn,
  type EnqueueDeliverableBuildPayload,
  type EnqueueDeliverableBuildResponse,
  type DeliverableTurnOutcome,
  type DeliverableChatPayload,
  type DeliverableChatEvent,
} from "@/types";
import {
  deliverableServiceError,
  readDeliverableServiceError,
} from "./deliverable-document";

/**
 * agent-service client for the Deliverables feature: context budgeting, the
 * server-side build and edit enqueue.
 *
 * Every call goes through the authenticated `/api/agent-proxy` route, which
 * forwards the JSON enqueue, status, and conversation requests.
 *
 * Initial builds are now owned by the server: the app first creates the
 * focalmonolith row, then enqueues agent-service with only that deliverable id.
 */

/* -------------------------------------------------------------------------- */
/* Paths                                                                      */
/* -------------------------------------------------------------------------- */

export const DELIVERABLE_CONVERSATION = "DELIVERABLE_CONVERSATION";

const AGENT_PROXY_BASE = "/api/agent-proxy";

/**
 * The agent-service version prefix. Every existing agent call in this repo is
 * `/api/agent-proxy/api/v1/...` while the deliverables spec names the routes
 * without it, so this is the single line to correct if integration proves the
 * deliverables routes are unprefixed.
 */
const AGENT_SERVICE_PREFIX = "/api/v1";

// agent-service mounts the router at `/deliverables` (plural) — see
// `src/api/routes/deliverables.py`. document-service's routes are singular
// (`/deliverable/...`); the two services genuinely differ here.
const DELIVERABLES_ROOT = `${AGENT_PROXY_BASE}${AGENT_SERVICE_PREFIX}/deliverables`;

/** Every agent-service deliverables path, in one place. */
export const agentServiceDeliverablePaths = {
  build: `${DELIVERABLES_ROOT}/build`,
  chat: `${DELIVERABLES_ROOT}/chat`,
  conversation: (deliverableId: string, beforeSeq?: number, limit?: number) => {
    const query = new URLSearchParams();
    if (beforeSeq !== undefined) query.set("beforeSeq", String(beforeSeq));
    if (limit !== undefined) query.set("limit", String(limit));
    const search = query.toString();
    return `${DELIVERABLES_ROOT}/${encodeURIComponent(deliverableId)}/conversation${
      search ? `?${search}` : ""
    }`;
  },
} as const;

/* -------------------------------------------------------------------------- */
/* Errors                                                                     */
/* -------------------------------------------------------------------------- */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const isAbortError = (error: unknown, signal?: AbortSignal): boolean =>
  signal?.aborted === true ||
  (isRecord(error) && error.name === "AbortError") ||
  (error instanceof DOMException && error.name === "AbortError");

/* -------------------------------------------------------------------------- */
/* Requests                                                                   */
/* -------------------------------------------------------------------------- */

interface AgentRequestOptions {
  signal?: AbortSignal;
}

/**
 * `POST /deliverables/build` — enqueue a server-owned build for a monolith row
 * that already exists in `GENERATING` state.
 */
export const enqueueDeliverableBuild = async (
  payload: EnqueueDeliverableBuildPayload,
  options?: AgentRequestOptions,
): Promise<EnqueueDeliverableBuildResponse> => {
  let response: Response;
  try {
    response = await fetch(agentServiceDeliverablePaths.build, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      cache: "no-store",
      body: JSON.stringify(payload),
      signal: options?.signal,
    });
  } catch (error) {
    if (isAbortError(error, options?.signal)) throw error;
    throw deliverableServiceError(ErrorSubcode.EXTERNAL_SERVICE_ERROR, 503);
  }

  if (response.status !== 202) {
    throw await readDeliverableServiceError(response);
  }

  // A 202 means the build is enqueued and server-owned from here, so the body
  // must never decide the outcome — and must not be waited on either. The agent
  // proxy forwards `upstream.body` as a stream under a 15-minute budget, so an
  // accepted response whose body agent-service has not closed would leave
  // `response.json()` pending and the advisor watching a Generate button that
  // spins long after the worker started. Cancel the body instead of reading it,
  // to release the connection rather than leave it dangling.
  //
  // Nothing reads `jobId` — progress arrives keyed by `deliverableId` over the
  // status subscription — so the field is kept only to hold the response shape.
  void response.body?.cancel().catch(() => undefined);

  return { jobId: "" };
};

/**
 * `GET /deliverables/:id/conversation` — the edit turns for one deliverable,
 * oldest first.
 *
 * agent-service owns this conversation, so it survives a refresh and is the
 * same for everyone who can open the deliverable. A turn does not necessarily
 * answer with a document: `outcome` says how it ended (an edit, an answer, a
 * question back, a refusal, a proposal awaiting confirmation, a failure) and
 * `replyText` carries the assistant's words when the answer was prose. Both are
 * optional — a turn stored before that vocabulary shipped carries `status`
 * alone, which still says whether the request landed, failed, or — while
 * `pending` — never came back.
 *
 * Turns are kept for the life of the deliverable, so this answers the newest
 * page. Pass `beforeSeq` — the oldest `seq` already held — to walk backwards.
 */
export const getDeliverableConversation = async (
  deliverableId: string,
  options?: AgentRequestOptions & { beforeSeq?: number; limit?: number },
): Promise<DeliverableConversationTurn[]> => {
  const response = await fetch(
    agentServiceDeliverablePaths.conversation(
      deliverableId,
      options?.beforeSeq,
      options?.limit,
    ),
    {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal: options?.signal,
    },
  );

  if (!response.ok) throw await readDeliverableServiceError(response);
  return (await response.json()) as DeliverableConversationTurn[];
};

export const DELIVERABLE_EDIT_STATUS = "DELIVERABLE_EDIT_STATUS";
export interface DeliverableEditJob {
  jobId: string;
  turnId: string;
  clientTurnId: string;
  requesterUserId: string;
  status: "queued" | "running" | "finalizing" | "succeeded" | "failed";
  stage: string | null;
  outcome: DeliverableTurnOutcome | null;
  resultRevisionId: string | null;
  failureReason: string | null;
  updatedAt: string;
}
export interface DeliverableEditStatus {
  activeJob: DeliverableEditJob | null;
  latestJob: DeliverableEditJob | null;
}
/**
 * Body of `POST /deliverables/edits`. Attachments are already stored on the
 * deliverable, so they are referenced by the monolith's `(filename, charCount)`
 * identity rather than sent again. A retry sends none.
 */
export interface DeliverableEditPayload {
  deliverableId: string;
  message: string;
  clientTurnId: string;
  /** The revision the advisor is looking at when they asked. */
  baseRevisionId: string;
  attachments?: { filename: string; charCount: number }[];
  retryOfJobId?: string;
}

const editJson = async <T>(path: string, body?: unknown): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(`${DELIVERABLES_ROOT}/${path}`, {
      method: body === undefined ? "GET" : "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      cache: "no-store",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw deliverableServiceError(ErrorSubcode.EXTERNAL_SERVICE_ERROR, 503);
  }
  if (!response.ok || (body !== undefined && response.status !== 202))
    throw await readDeliverableServiceError(response);
  return response.json();
};
export const getDeliverableEditStatus = (id: string) =>
  editJson<DeliverableEditStatus>(`${encodeURIComponent(id)}/edit-status`);
export const enqueueDeliverableEdit = (payload: DeliverableEditPayload) =>
  editJson<
    Pick<DeliverableEditJob, "jobId" | "turnId" | "clientTurnId" | "status">
  >("edits", payload);

export type { DeliverableChatPayload } from "@/types";

const chatOutcomes: DeliverableTurnOutcome[] = [
  "answered",
  "clarification_requested",
  "blocked",
  "data_requested",
  "confirmation_requested",
  "edit_queued",
  "failed",
];

const normalizeChatEvent = (value: unknown): DeliverableChatEvent | null => {
  if (!isRecord(value)) return null;
  switch (value.type) {
    case "status":
      return typeof value.stage === "string"
        ? { type: "status", stage: value.stage }
        : null;
    case "token":
    case "message":
      if (
        typeof value.content !== "string" ||
        (value.type === "message" && value.kind !== "answer")
      )
        return null;
      return value.type === "token"
        ? { type: "token", content: value.content }
        : { type: "message", kind: "answer", content: value.content };
    case "error":
      return typeof value.message === "string"
        ? { type: "error", message: value.message }
        : null;
    case "turn":
      if (
        typeof value.turnId !== "string" ||
        !value.turnId ||
        !Number.isSafeInteger(value.seq) ||
        typeof value.outcome !== "string" ||
        !chatOutcomes.includes(value.outcome as DeliverableTurnOutcome)
      )
        return null;
      return {
        type: "turn",
        turnId: value.turnId,
        seq: value.seq as number,
        outcome: value.outcome as DeliverableTurnOutcome,
      };
    case "proposal": {
      const proposal = value.proposal;
      if (
        !isRecord(proposal) ||
        typeof proposal.proposalId !== "string" ||
        typeof proposal.summary !== "string" ||
        !Array.isArray(proposal.expectedChanges) ||
        !proposal.expectedChanges.every(
          (change) => typeof change === "string",
        ) ||
        !(
          proposal.parentRevisionId === null ||
          typeof proposal.parentRevisionId === "string"
        )
      )
        return null;
      return {
        type: "proposal",
        proposal: {
          proposalId: proposal.proposalId,
          summary: proposal.summary,
          expectedChanges: proposal.expectedChanges,
          parentRevisionId: proposal.parentRevisionId,
        },
      };
    }
    case "data_requested": {
      const request = value.dataRequest;
      if (
        typeof value.turnId !== "string" ||
        !isRecord(request) ||
        typeof request.requestId !== "string" ||
        typeof request.summary !== "string" ||
        typeof request.question !== "string" ||
        !["pending", "running", "complete", "declined", "failed"].includes(
          String(request.status),
        ) ||
        !(
          request.interruptId === undefined ||
          typeof request.interruptId === "string"
        )
      )
        return null;
      return {
        type: "data_requested",
        turnId: value.turnId,
        dataRequest: {
          requestId: request.requestId,
          summary: request.summary,
          question: request.question,
          status: request.status as
            "pending" | "running" | "complete" | "declined" | "failed",
          ...(request.interruptId === undefined
            ? {}
            : { interruptId: request.interruptId }),
        },
      };
    }
    default:
      return null;
  }
};

/** Stream one interactive turn; a transport failure is retried with a new message. */
export const streamDeliverableChat = async (
  payload: DeliverableChatPayload,
  onEvent: (event: DeliverableChatEvent) => void,
): Promise<void> => {
  let response: Response;
  try {
    response = await fetch(agentServiceDeliverablePaths.chat, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      cache: "no-store",
      body: JSON.stringify(payload),
    });
  } catch {
    throw deliverableServiceError(ErrorSubcode.EXTERNAL_SERVICE_ERROR, 503);
  }
  if (!response.ok) throw await readDeliverableServiceError(response);
  if (!response.body)
    throw deliverableServiceError(ErrorSubcode.EXTERNAL_SERVICE_ERROR, 503);
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let data: string[] = [];
  let terminal = false;
  let failed = false;
  const line = (text: string) => {
    if (text !== "") {
      if (text.startsWith("data:")) data.push(text.slice(5).replace(/^ /, ""));
      return;
    }
    let value: unknown;
    try {
      value = JSON.parse(data.join("\n"));
    } catch {
      data = [];
      return;
    }
    data = [];
    const event = normalizeChatEvent(value);
    if (!event) return;
    if (event.type === "error") failed = true;
    if (event.type === "turn") terminal = true;
    onEvent(event);
  };
  try {
    while (true) {
      const chunk = await reader.read();
      buffer += decoder.decode(chunk.value, { stream: !chunk.done });
      let end: number;
      while ((end = buffer.indexOf("\n")) >= 0) {
        line(buffer.slice(0, end).replace(/\r$/, ""));
        buffer = buffer.slice(end + 1);
      }
      if (chunk.done || terminal) break;
    }
  } finally {
    if (terminal) void reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
  if (failed || !terminal)
    throw deliverableServiceError(ErrorSubcode.EXTERNAL_SERVICE_ERROR, 503);
};
